/* global PiniaSetupStoreHelpers */
import type { UseWebSocketReturn } from '@vueuse/core';

import { reactive, ref, toRefs } from 'vue';
import { defineStore } from 'pinia';
import { useWebSocket } from '@vueuse/core';
import _cloneDeep from 'lodash/cloneDeep'; // use `_cloneDeep` to avoid accidentally changing `defState`

import { useAuthStore } from '@/stores/useAuthStore';
import { sendError } from '@/helpers/sentry';

const socketURL = import.meta.env.VITE_APP_WEBSOCKET_V2;

export interface DefWebSocketState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  message: any;
  isAuth: boolean | undefined;
}

const defWebSocketState: DefWebSocketState = {
  message: undefined,
  isAuth: undefined,
};

function webSocketStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defWebSocketState));

  const authStore = useAuthStore();

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const _webSocket = ref<UseWebSocketReturn<any>>();

  async function $reset(): Promise<void> {
    Object.assign(state, _cloneDeep(defWebSocketState));
    if (_webSocket.value) {
      _webSocket.value.open();
    }
  }

  function init() {
    _webSocket.value = useWebSocket(socketURL, {
      onConnected(ws) {
        // console.log('onConnected');
        const msg = {
          type: 'authenticate',
          token: authStore.authData.access,
        };
        ws.send(JSON.stringify(msg));
      },
      onDisconnected(ws, event) {
        // console.log('onDisconnected', event);
      },
      onError(ws, event) {
        // console.log('onError', event);
        sendError('Error WebSocket', '', event);
      },
      onMessage(ws, event) {
        let _data;
        try {
          _data = JSON.parse(event.data);
        } catch (err) {
          sendError(err, 'JSON.parse WebSocket');
        }
        if (_data) {
          // if (_data.type === 'ping') return;
          if (_data.type === 'pong') return;
          if (_data.type === 'heartbeat') return;
          if (_data.type === 'authentication_status') {
            if (_data.status === 'success') {
              state.isAuth = true;
            } else {
              state.isAuth = false;
            }
          }
          if (_data.type === 'message') {
            state.message = _data;
          }
        }
        // console.log('onMessage', event);
      },
      heartbeat: {
        message: JSON.stringify({ type: 'ping' }),
        interval: 30000, // 30 seconds (same as server)
        pongTimeout: 10000, // 10 seconds grace period
      },
      autoReconnect: {
        retries: 3,
        delay: 1000,
        onFailed() {
          console.error('Failed to reconnect WebSocket');
          sendError('Failed to reconnect WebSocket');
        },
      },
    });
  }

  function getWebSocket() {
    return _webSocket;
  }

  return {
    ...toRefs(state),
    $reset,
    init,
    getWebSocket,
  };
}

export const useWebSocketStore = defineStore('webSocket', webSocketStoreSetup);
